import { useState, useRef, useCallback, useEffect } from 'react'
import { Button } from '@highlight/ui'
import { Check, Copy } from 'lucide-react'
import ShellIpc from '@renderer/ipc/ShellIpc.renderer'
import type { TmuxStatus } from '@shared/types/ShellTypes'

interface InstallCommand {
  label: string
  command: string
}

function buildInstallCommands(status: TmuxStatus | null): Array<InstallCommand> {
  if (!status) return []

  if (status.platform === 'darwin') {
    return [
      { label: 'Homebrew', command: 'brew install tmux' },
      { label: 'MacPorts', command: 'sudo port install tmux' },
    ]
  }

  if (status.platform === 'win32') {
    // tmux does not run natively on Windows; point users to WSL.
    return [{ label: 'WSL (Ubuntu)', command: 'wsl -- sudo apt install tmux' }]
  }

  if (status.platform === 'linux') {
    const distroId = status.linuxDistroId ?? ''
    const distroLike = status.linuxDistroLike ?? ''
    const hay = `${distroId} ${distroLike}`

    // Prefer the detected distro's command at the top.
    const all: Array<{ match: RegExp; cmd: InstallCommand }> = [
      { match: /debian|ubuntu/, cmd: { label: 'Debian / Ubuntu', command: 'sudo apt install tmux' } },
      { match: /fedora/, cmd: { label: 'Fedora', command: 'sudo dnf install tmux' } },
      { match: /rhel|centos/, cmd: { label: 'RHEL / CentOS', command: 'sudo yum install tmux' } },
      { match: /arch/, cmd: { label: 'Arch Linux', command: 'sudo pacman -S tmux' } },
      { match: /suse/, cmd: { label: 'openSUSE', command: 'sudo zypper install tmux' } },
    ]

    const primary = all.find((entry) => entry.match.test(hay))
    const rest = all.filter((entry) => entry !== primary).map((entry) => entry.cmd)
    return primary ? [primary.cmd, ...rest] : all.map((entry) => entry.cmd)
  }

  return []
}

function CommandRow({ label, command }: InstallCommand) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command)
      if (timerRef.current) clearTimeout(timerRef.current)
      setCopied(true)
      timerRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard write failed; leave state alone
    }
  }, [command])

  return (
    <span className="bg-foreground/[3%] flex items-center gap-2 rounded-md border px-2.5 py-1.5">
      <span className="text-foreground-placeholder w-24 shrink-0 text-[11px] font-medium">{label}</span>
      <code className="text-foreground-secondary min-w-0 flex-1 truncate font-mono text-xs">{command}</code>
      <Button
        shape="square"
        size="sm"
        variant="outline"
        aria-label={`Copy ${label} install command`}
        onClick={handleCopy}
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      </Button>
    </span>
  )
}

interface TmuxInstallHintProps {
  /** Optional pre-fetched status to avoid an IPC round-trip. */
  status?: TmuxStatus | null
  /** Title shown above the command list. */
  title?: string
  /** Optional subtext shown below the title. */
  description?: string
  className?: string
}

/**
 * Renders a "tmux is required" hint with copyable, platform-aware install commands.
 * Fetches platform info from the main process when `status` is not supplied.
 *
 * Uses `<span>` elements throughout so it can safely render inside `Cell.Banner`,
 * which wraps children in a `<span>` — nesting `<div>` in `<span>` triggers
 * React hydration warnings and is invalid HTML.
 */
export function TmuxInstallHint({
  status: statusProp,
  title = 'tmux is required to run terminal sessions',
  description,
  className,
}: TmuxInstallHintProps) {
  const [status, setStatus] = useState<TmuxStatus | null>(statusProp ?? null)

  useEffect(() => {
    if (statusProp !== undefined) {
      setStatus(statusProp)
      return
    }
    let cancelled = false
    ShellIpc.getTmuxStatus()
      .then((s) => {
        if (!cancelled) setStatus(s)
      })
      .catch(() => {
        if (!cancelled) setStatus({ available: false, platform: 'unknown' })
      })
    return () => {
      cancelled = true
    }
  }, [statusProp])

  const commands = buildInstallCommands(status)
  const resolvedDescription = description ?? 'Install tmux, then try again. Copy a command below to get started.'

  return (
    <span className={`flex flex-col ${className ?? ''}`}>
      <span className="text-foreground text-sm font-medium">{title}</span>
      <span className="text-foreground-secondary mt-0.5 text-xs">{resolvedDescription}</span>
      {commands.length > 0 && (
        <span className="mt-2 flex flex-col gap-1.5">
          {commands.map((c) => (
            <CommandRow key={c.label} label={c.label} command={c.command} />
          ))}
        </span>
      )}
    </span>
  )
}

/** Returns true when the given error message is the "tmux not available" error. */
export function isTmuxUnavailableError(errorMessage: string | undefined): boolean {
  if (!errorMessage) return false
  return /tmux not available/i.test(errorMessage)
}
